import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useZoom } from 'context/ZoomContext';
import { useModelFilters } from 'dashboard409a/hooks/useModelFilters';
import { getInitials } from 'utilities/getInitials';
import { ModelFiltersProps } from './filters.props';

export const ModelFilters = ({ statusOptions, statusOptionsLoading, onFiltersChange }: ModelFiltersProps) => {
  const { zoomLevel } = useZoom();

  const {
    searchParam,
    searchStatus,
    fromDueDate,
    toDueDate,
    selectedPreparers,
    selectedReviewers,
    preparerSearchInput,
    reviewerSearchInput,
    sortedPreparers,
    sortedReviewers,
    isLoadingPreparers,
    isLoadingReviewers,
    handleSearchParamChange,
    handleSearchStatusChange,
    handleFromDueDateChange,
    handleToDueDateChange,
    handlePreparersChange,
    handleReviewersChange,
    setPreparerSearchInput,
    setReviewerSearchInput,
    cleanFilters,
  } = useModelFilters({ onFiltersChange });

  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ marginTop: 2, marginLeft: 2 }}>
      <TextField label="Search" variant="outlined" value={searchParam} onChange={handleSearchParamChange} />
      <Select
        label="Status"
        variant="outlined"
        value={searchStatus}
        onChange={handleSearchStatusChange}
        sx={{ width: 200 }}>
        <MenuItem disabled value="default">
          Select Status
        </MenuItem>
        <MenuItem value="All">All</MenuItem>
        {statusOptionsLoading ? (
          <MenuItem disabled> Loading... </MenuItem>
        ) : (
          statusOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))
        )}
      </Select>
      <DatePicker
        label="From Due Date"
        value={fromDueDate}
        onChange={handleFromDueDateChange}
        slotProps={{
          textField: {
            variant: 'outlined',
          },
        }}
      />
      <DatePicker
        label="To Due Date"
        value={toDueDate}
        onChange={handleToDueDateChange}
        minDate={fromDueDate || undefined}
        slotProps={{
          textField: {
            variant: 'outlined',
          },
        }}
      />
      <Autocomplete
        multiple
        options={sortedReviewers}
        getOptionLabel={option => {
          const fullName = [option.first_name, option.last_name].filter(Boolean).join(' ');
          return fullName || option.email || option.username || '';
        }}
        slotProps={{
          popper: {
            sx: {
              width: `${zoomLevel * 300}px !important`,
            },
          },
        }}
        disableCloseOnSelect
        value={selectedReviewers}
        onChange={handleReviewersChange}
        onInputChange={(_event, newInputValue) => {
          setReviewerSearchInput(newInputValue);
        }}
        inputValue={reviewerSearchInput}
        loading={isLoadingReviewers}
        renderInput={params => (
          <TextField {...params} label="Select Reviewers" variant="outlined" placeholder="Search for reviewers..." sx={{ maxWidth: 300 }} />
        )}
        renderTags={value => (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              maxHeight: 100,
              alignItems: 'flex-start',
              width: '100%',
              py: 0.5
            }}
          >
            {value.map(option => (
              <Avatar key={option.id} sx={{ width: 32, height: 32 }}>
                {getInitials({ name: `${option.first_name} ${option.last_name}` })}
              </Avatar>
            ))}
          </Box>
        )}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <li key={option.id} {...optionProps} style={{ padding: 0 }}>
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
              {[option.first_name, option.last_name].join(' ') || option.email || option.username || ''}
            </li>
          );
        }}
        sx={{ minWidth: 300 }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
      <Autocomplete
        multiple
        options={sortedPreparers}
        getOptionLabel={option => {
          const fullName = [option.first_name, option.last_name].filter(Boolean).join(' ');
          return fullName || option.email || option.username || '';
        }}
        slotProps={{
          popper: {
            sx: {
              width: `${zoomLevel * 300}px !important`,
            },
          },
        }}
        disableCloseOnSelect
        value={selectedPreparers}
        onChange={handlePreparersChange}
        onInputChange={(_event, newInputValue) => {
          setPreparerSearchInput(newInputValue);
        }}
        inputValue={preparerSearchInput}
        loading={isLoadingPreparers}
        renderInput={params => (
          <TextField {...params} label="Select Preparers" variant="outlined" placeholder="Search for preparers..." sx={{ maxWidth: 300 }} />
        )}
        renderTags={value => (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              maxHeight: 100,
              alignItems: 'flex-start',
              width: '100%',
              py: 0.5
            }}
          >
            {value.map(option => (
              <Avatar key={option.id} sx={{ width: 32, height: 32 }}>
                {getInitials({ name: `${option.first_name} ${option.last_name}` })}
              </Avatar>
            ))}
          </Box>
        )}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <li key={option.id} {...optionProps} style={{ padding: 0 }}>
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
              {[option.first_name, option.last_name].join(' ') || option.email || option.username || ''}
            </li>
          );
        }}
        sx={{ minWidth: 300 }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
      <Tooltip title="Clear filters">
        <IconButton onClick={cleanFilters}>
          <FilterAltOffIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
